import convert from "color-convert";

export type ColorCieLab = [number, number, number];
export type ColorCieLch = [number, number, number];
export type ColorRgb = [number, number, number];
interface CIEConstants {
  kL: number;
  kC: number;
  kH: number;
}

export function roundColor(value: Array<number>, digits = 2) {
  return value.map(
    (v) => Math.round(v * 10 ** digits) / 10 ** digits,
  ) as ColorCieLab;
}

export function rgbToLab(value: ColorRgb): ColorCieLab {
  return convert.rgb.lab.raw(value);
}

export function labToRgb(value: ColorCieLab): ColorRgb {
  return convert.lab.rgb.raw(value);
}

export function labToLch(value: ColorCieLab): ColorCieLch {
  return convert.lab.lch.raw(value);
}

export function lchToLab(value: ColorCieLch): ColorCieLab {
  return convert.lch.lab.raw(value);
}

const rad2deg = (rad: number): number => (360 * rad) / (2 * Math.PI);

const deg2rad = (deg: number): number => (2 * Math.PI * deg) / 360;

const calculateC = (a: number, b: number): number => Math.sqrt(a ** 2 + b ** 2);
const calculateG = (Cbar: number): number =>
  0.5 * (1 - Math.sqrt(Cbar ** 7 / (Cbar ** 7 + 25 ** 7)));
const calculateHPrime = (b: number, aPrime: number): number => {
  const hPrime = rad2deg(Math.atan2(b, aPrime));
  return hPrime < 0 ? hPrime + 360 : hPrime;
};
const calculateHBarPrime = (h1prime: number, h2prime: number): number =>
  Math.abs(h1prime - h2prime) > 180
    ? (h1prime + h2prime + 360) / 2
    : (h1prime + h2prime) / 2;

const calculateT = (Habbar: number): number =>
  1 -
  0.17 * Math.cos(deg2rad(Habbar - 30)) +
  0.24 * Math.cos(deg2rad(2 * Habbar)) +
  0.32 * Math.cos(deg2rad(3 * Habbar + 6)) -
  0.2 * Math.cos(deg2rad(4 * Habbar - 63));

const calculateDeltaHPrime = (h2prime: number, h1prime: number): number => {
  const diff = h2prime - h1prime;
  if (Math.abs(diff) <= 180) return diff;
  return diff > 180 ? diff - 360 : diff + 360;
};

const calculateSL = (meanL: number): number =>
  1 + (0.015 * (meanL - 50) ** 2) / Math.sqrt(20 + (meanL - 50) ** 2);

const calculateSC = (Cabbar: number): number => 1 + 0.045 * Cabbar;

const calculateSH = (Cabbar: number, T: number): number =>
  1 + 0.015 * Cabbar * T;

const calculateRT = (Cabbar: number, Habbar: number): number => {
  const deltaTheta = 30 * Math.exp(-(((Habbar - 275) / 25) ** 2));
  const RC = 2 * Math.sqrt(Cabbar ** 7 / (Cabbar ** 7 + 25 ** 7));
  return -RC * Math.sin(2 * deg2rad(deltaTheta));
};

export function deltaE(a: ColorCieLab, b: ColorCieLab) {
  const lab1 = {
    L: a[0] ?? 0,
    A: a[1] ?? 0,
    B: a[2] ?? 0,
  };

  const lab2 = {
    L: b[0] ?? 0,
    A: b[1] ?? 0,
    B: b[2] ?? 0,
  };

  const constants: CIEConstants = { kL: 1, kC: 1, kH: 1 };

  // Step 1: Calculate C1, C2, Cbar
  const C1 = calculateC(lab1.A, lab1.B);
  const C2 = calculateC(lab2.A, lab2.B);
  const Cbar = (C1 + C2) / 2;

  // Step 2: Calculate a'1, a'2, C'1, C'2
  const G = calculateG(Cbar);
  const a1Prime = (1 + G) * lab1.A;
  const a2Prime = (1 + G) * lab2.A;
  const C1Prime = calculateC(a1Prime, lab1.B);
  const C2Prime = calculateC(a2Prime, lab2.B);
  const CabbarPrime = (C1Prime + C2Prime) / 2;

  // Step 3: Calculate h'1, h'2
  const h1Prime = calculateHPrime(lab1.B, a1Prime);
  const h2Prime = calculateHPrime(lab2.B, a2Prime);

  // Step 4: Calculate H'ab
  const HabbarPrime = calculateHBarPrime(h1Prime, h2Prime);

  // Step 5: Calculate T
  const T = calculateT(HabbarPrime);

  // Step 6: Calculate ΔH'
  const deltaHPrime = calculateDeltaHPrime(h2Prime, h1Prime);

  // Step 7: Calculate ΔL', ΔC', ΔH'
  const deltaLPrime = lab2.L - lab1.L;
  const deltaCPrime = C2Prime - C1Prime;
  const deltaHPrimeFinal =
    2 * Math.sqrt(C1Prime * C2Prime) * Math.sin(deg2rad(deltaHPrime) / 2);

  // Step 8: Calculate SL, SC, SH
  const meanL = (lab1.L + lab2.L) / 2;
  const SL = calculateSL(meanL);
  const SC = calculateSC(CabbarPrime);
  const SH = calculateSH(CabbarPrime, T);

  // Step 9: Calculate RT
  const RT = calculateRT(CabbarPrime, HabbarPrime);

  // Final calculation
  return Math.sqrt(
    (deltaLPrime / (constants.kL * SL)) ** 2 +
      (deltaCPrime / (constants.kC * SC)) ** 2 +
      (deltaHPrimeFinal / (constants.kH * SH)) ** 2 +
      RT *
        (deltaCPrime / (constants.kC * SC)) *
        (deltaHPrimeFinal / (constants.kH * SH)),
  );
}

export function deltaAngle(a: number, b: number) {
  const res = (((a - b) % 360) + 360) % 360;
  return res > 180 ? res - 360 : res;
}

export function labToCss(value: ColorCieLab) {
  const rgbColor = labToRgb(value);
  return `rgb(${rgbColor.map((v) => v.toFixed(5)).join(" ")})`;
}

export function rgbToCss(rgbColor: ColorRgb) {
  return `rgb(${rgbColor.map((v) => v.toFixed(5)).join(" ")})`;
}

export function hexToRgb(hex: string): ColorRgb {
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return [
    parseInt(result![1], 16),
    parseInt(result![2], 16),
    parseInt(result![3], 16),
  ];
}
